import React, { useEffect, useMemo, useState } from "react"
import { Button, Form, Input, InputNumber, Modal, Space, Switch, Table, Typography } from "antd"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { notifications } from "notifications"
import { UploadComponent } from "components"
import { BuffPlayThemeConfigType } from "../../store/types"
import { dateRangeValidator } from "../../helper"
import { themeConfigColumns, ThemeConfigRow } from "./columns"

const { Title, Text } = Typography

export const ThemeConfig = observer(() => {
  const { dataStore: { managingJsonStore } } = useStore()

  const [themeConfig, setThemeConfig] = useState<BuffPlayThemeConfigType | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [editDateRange, setEditDateRange] = useState<string | null>(null)
  const [form] = Form.useForm()

  const [images, setImages] = useState({
    overlay: "",
    marketplace: "",
    earn: "",
    loading: "",
  })

  useEffect(() => {
    managingJsonStore.getData()
  }, [managingJsonStore])

  useEffect(() => {
    const config = managingJsonStore?.itemTypes?.find(
      item => item.type === "buffPlayThemeConfig" && item.name === "buffPlayThemeConfig",
    )

    if (config) {
      const data = config.data as BuffPlayThemeConfigType
      setThemeConfig(data)
    }
  }, [managingJsonStore.itemTypes])


  const tableData = useMemo(() => {
    if (!themeConfig) return []

    return Object.entries(themeConfig).map(([dateRange, config]) => ({
      dateRange,
      ...config,
    }))
  }, [themeConfig])

  const openCreate = () => {
    setEditDateRange(null)
    setModalVisible(true)
    form.resetFields()
    form.setFieldsValue({ active: true })
    setImages({ overlay: "", marketplace: "", earn: "", loading: "" })
  }

  const openEdit = (row: ThemeConfigRow) => {
    setEditDateRange(row.dateRange)
    setModalVisible(true)

    form.setFieldsValue({
      dateRange: row.dateRange,
      duration: row.overlayProps?.duration,
      itemCount: row.overlayProps?.itemCount,
      active: row.active,
    })

    setImages({
      overlay: row.overlayProps?.src || "",
      marketplace: row.marketplaceBanner || "",
      earn: row.earnBanner || "",
      loading: row.loadingIcon || "",
    })
  }

  const removeThemeConfig = (dateRange: string) => {
    if (!themeConfig) return

    Modal.confirm({
      title: "Delete theme config",
      content: `Remove theme config for "${dateRange}"?`,
      okType: "danger",
      onOk: async() => {
        try {
          const { [dateRange]: _, ...rest } = themeConfig
          const isLast = Object.keys(rest).length === 0

          if (isLast) {
            await managingJsonStore.update({
              type: "buffPlayThemeConfig",
              name: "buffPlayThemeConfig",
              data: {},
            })
          } else {
            await managingJsonStore.update({
              type: "buffPlayThemeConfig",
              name: "buffPlayThemeConfig",
              data: rest,
            })
          }

          notifications.success("Theme config removed")
          await managingJsonStore.getData()
        } catch (err: any) {
          notifications.error(err?.message || "Failed to remove theme config")
        }
      },
    })
  }

  const onFinishHandler = async(values: {dateRange: string, duration?: number, itemCount?: number, active: boolean}) => {
    try {
      const rangeConfig = {
        overlayProps: images.overlay
          ? {
            src: images.overlay,
            duration: values.duration ?? undefined,
            itemCount: values.itemCount ?? undefined,
          }
          : null,
        marketplaceBanner: images.marketplace || null,
        earnBanner: images.earn || null,
        loadingIcon: images.loading || null,
        active: values.active,
      }

      if (!themeConfig) {
        const newData: BuffPlayThemeConfigType = { [values.dateRange]: rangeConfig }

        await managingJsonStore.create({
          type: "buffPlayThemeConfig",
          name: "buffPlayThemeConfig",
          data: newData,
        })
      } else {
        const nextData: BuffPlayThemeConfigType = { ...themeConfig }

        if (editDateRange && editDateRange !== values.dateRange) {
          delete nextData[editDateRange]
        }

        nextData[values.dateRange] = rangeConfig

        await managingJsonStore.update({
          type: "buffPlayThemeConfig",
          name: "buffPlayThemeConfig",
          data: nextData,
        })
      }
      setModalVisible(false)
      setEditDateRange(null)
      notifications.success("Theme configuration saved successfully")
      await managingJsonStore.getData()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to save")
    }
  }

  const handleActiveChange = async(dateRange: string, checked: boolean) => {
    if (!themeConfig) return
    try {
      const nextData = { ...themeConfig }
      nextData[dateRange] = { ...nextData[dateRange], active: checked }

      await managingJsonStore.update({
        type: "buffPlayThemeConfig",
        name: "buffPlayThemeConfig",
        data: nextData,
      })

      notifications.success(`Theme ${checked ? "enabled" : "disabled"}`)
      await managingJsonStore.getData()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to update theme")
    }
  }

  const columns = [
    ...themeConfigColumns,
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active: boolean, row: ThemeConfigRow) => (
        <Switch
          checked={active}
          onChange={checked => handleActiveChange(row.dateRange, checked)}
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, row: ThemeConfigRow) => (
        <Space>
          <Button type="primary" onClick={() => openEdit(row)}>Edit</Button>
          <Button danger onClick={() => removeThemeConfig(row.dateRange)}>Delete</Button>
        </Space>
      ),
    },
  ]


  return (
    <>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Title level={2}>Theme Configuration</Title>
        <Button type="primary" onClick={openCreate}>
          Add new theme
        </Button>
      </Space>

      <Table
        rowKey={record => record.dateRange}
        pagination={false}
        scroll={{ x: true }}
        dataSource={tableData}
        columns={columns}
      />

      <Modal
        open={modalVisible}
        title={editDateRange ? `Edit theme config for ${editDateRange}` : "Add new theme config"}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={onFinishHandler}>
          <Form.Item
            name="dateRange"
            label="Date range:"
            rules={[
              { required: true, message: "Date range is required" },
              dateRangeValidator({
                themeConfig,
                currentRange: editDateRange,
              }),
            ]}
          >
            <Input placeholder="08/12-30/12" />
          </Form.Item>
          <p style={{ margin: 0 }}>Overlay:</p>
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: "12px" }}>
            Used as falling items. Recommended: square (1:1), 256–512px, PNG
          </Text>
          <UploadComponent
            image={images.overlay}
            setImage={url => setImages(prev => ({ ...prev, overlay: url }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            imgStyle={{ width: 72, height: 72, textAlign: "center", display: "flex" }}
            placeholder="Upload image"
          />

          <Space style={{ width: "100%" }} size="large">
            <Form.Item name="duration" label="Duration:">
              <InputNumber min={1} placeholder="Duration in seconds" />
            </Form.Item>
            <Form.Item name="itemCount" label="Item count:">
              <InputNumber min={1} placeholder="Number of items" />
            </Form.Item>
          </Space>

          <p style={{ margin: 0 }}>Earn banner:</p>
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: "12px" }}>
            Background image for Earn card. Recommended: Horizontal 3:1 (e.g., 1200×400), JPG/PNG
          </Text>
          <UploadComponent
            image={images.earn}
            setImage={url => setImages(prev => ({ ...prev, earn: url }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            imgStyle={{ width: 72, height: 72, textAlign: "center", display: "flex" }}
            placeholder="Upload image"
          />

          <p style={{ margin: 0, marginTop: 12 }}>Marketplace banner:</p>
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: "12px" }}>
            Small decorative image. Recommended: Square or 3:4, ~512px PNG
          </Text>
          <UploadComponent
            image={images.marketplace}
            setImage={url => setImages(prev => ({ ...prev, marketplace: url }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            imgStyle={{ width: 72, height: 72, textAlign: "center", display: "flex" }}
            placeholder="Upload image"
          />

          <p style={{ margin: 0, marginTop: 12 }}>Loading icon:</p>
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: "12px" }}>
            Displayed while page loads. Recommended: Square (1:1), 512–1024px, PNG or SVG
          </Text>
          <UploadComponent
            image={images.loading}
            setImage={url => setImages(prev => ({ ...prev, loading: url }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            imgStyle={{ width: 72, height: 72, textAlign: "center", display: "flex" }}
            placeholder="Upload image"
          />
          <Form.Item
            name="active"
            label="Active:"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Space
            style={{ width: "100%", justifyContent: "space-between", marginTop: 24 }}
          >
            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  )
})
